import { FC, useState, useRef, useEffect, ReactNode } from "react";

type Position = "left" | "right" | "top" | "bottom";

interface PopoverProps {
    children: ReactNode;
    text: string;
    defaultPosition?: Position; // Alapértelmezett pozíció beállítása (alapértelmezett: 'top')
}

const Popover: FC<PopoverProps> = ({ children, text, defaultPosition = "top" }) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<Position>(defaultPosition);
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    // Bezárás, ha máshova kattintunk (mobil + desktop támogatás)
    useEffect(() => {
        const handleClickOutside = (event: PointerEvent) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener("pointerdown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("pointerdown", handleClickOutside);
        };
    }, [open]);

    // Pozíció számítása figyelembe véve a defaultPosition-t
    useEffect(() => {
        if (!open) return;

        const popover = popoverRef.current;
        const button = buttonRef.current;

        if (!popover || !button) return;

        const popoverRect = popover.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let newPosition: Position = defaultPosition;

        // Ha az alapértelmezett pozíció nem jó, keres másik opciót
        if (newPosition === "left" && buttonRect.left - popoverRect.width < 0) {
            newPosition = "right";
        }
        if (newPosition === "right" && buttonRect.right + popoverRect.width > viewportWidth) {
            newPosition = "bottom";
        }
        if (newPosition === "bottom" && buttonRect.bottom + popoverRect.height > viewportHeight) {
            newPosition = "top";
        }
        if (newPosition === "top" && buttonRect.top - popoverRect.height < 0) {
            newPosition = "bottom"; // Ha sehol nem fér el, visszaáll a default-ra
        }

        setPosition(newPosition);
    }, [open, defaultPosition]);

    // Hover események
    const handleMouseEnter = () => setOpen(true);
    const handleMouseLeave = () => setOpen(false);

    return (
        <div className="relative inline-block">
            <div
                ref={buttonRef}
                onMouseEnter={handleMouseEnter} // Hoverre aktiváljuk
                onMouseLeave={handleMouseLeave} // Hover elhagyása után elrejtjük
                className="cursor-pointer"
            >
                {children}
            </div>

            {open && (
                <div
                    ref={popoverRef}
                    className={"popover " + position}
                    style={{
                        top:
                            position === "top"
                                ? "calc(100% + 10px)"
                                : position === "bottom"
                                ? "calc(100% + 10px)"
                                : "50%",
                        left:
                            position === "left"
                                ? "auto"
                                : position === "right"
                                ? "calc(100% + 10px)"
                                : "50%",
                        right:
                            position === "left"
                                ? "calc(100% + 10px)"
                                : position === "right"
                                ? "auto"
                                : "auto",
                        transform:
                            position === "top" || position === "bottom"
                                ? "translateX(-50%)"
                                : "translateY(-50%)",
                    }}
                >
                    <p>{text}</p>
                </div>
            )}
        </div>
    );
};

export default Popover;